import React from 'react';
import { CheckCircle, Pencil, Prohibit } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const ACTION_META = {
  fix: {
    icon: <Pencil size={20} className="text-indigo-600" />,
    title: 'Fix the claim',
    placeholder: 'e.g., Wrong diagnosis code, will correct',
    confirmLabel: 'Reopen as Draft',
    confirmClass: 'bg-indigo-900 hover:bg-indigo-800 text-white',
    description: (claimNumber) => (
      <>
        <strong>{claimNumber}</strong> will be reopened as a Draft so you can correct it. Once
        fixed, send it back through &ldquo;Send to ODM&rdquo;.
      </>
    ),
  },
  void: {
    icon: <Prohibit size={20} className="text-red-600" />,
    title: 'Void the claim',
    placeholder: 'e.g., Duplicate of CLM-12345',
    confirmLabel: 'Void claim',
    confirmClass: 'bg-red-600 hover:bg-red-700 text-white',
    description: (claimNumber) => (
      <>
        <strong>{claimNumber}</strong> will be marked Voided. The claim record stays in the
        database for audit but is excluded from billing and metrics. This action cannot be undone
        from the UI.
      </>
    ),
  },
  resolve: {
    icon: <CheckCircle size={20} className="text-emerald-600" />,
    title: 'Mark resolved',
    placeholder: 'e.g., Resolved with CareSource via phone, ref# 8821',
    confirmLabel: 'Mark resolved',
    confirmClass: 'bg-emerald-700 hover:bg-emerald-800 text-white',
    description: (claimNumber) => (
      <>
        <strong>{claimNumber}</strong> will be flagged as administratively resolved (e.g., handled
        offline with the payer). It will drop off the &ldquo;needs action&rdquo; filter but its
        history is preserved.
      </>
    ),
  },
};

/**
 * Triage confirmation dialog — used for Fix / Void / Resolve actions on
 * Rejected claims. `triageClaim` shape: `{ id, action, claim_number } | null`.
 */
const TriageDialog = ({
  triageClaim,
  triageNotes,
  setTriageNotes,
  triaging,
  onClose,
  onConfirm,
}) => {
  const meta = triageClaim ? ACTION_META[triageClaim.action] : null;
  return (
    <Dialog open={!!triageClaim} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="triage-dialog">
        {triageClaim && meta && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {meta.icon} {meta.title}
              </DialogTitle>
              <DialogDescription>{meta.description(triageClaim.claim_number)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label htmlFor="triage-notes">
                Notes <span className="text-slate-400">(optional, recommended)</span>
              </Label>
              <Input
                id="triage-notes"
                value={triageNotes}
                onChange={(e) => setTriageNotes(e.target.value)}
                placeholder={meta.placeholder}
                data-testid="triage-notes-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={triaging}>
                Cancel
              </Button>
              <Button
                onClick={onConfirm}
                disabled={triaging}
                className={meta.confirmClass}
                data-testid="triage-confirm-btn"
              >
                {triaging ? 'Working…' : meta.confirmLabel}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TriageDialog;
